/*
  Reject Documents service is used to centralise all the operations that are required
  for rejection of documents. All the actions are common for all the roles.
*/

import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { RoleTypes } from 'appRootPath/models/commercial-papers.model';
import { ConfirmDialogBoxModalComponent } from 'appRootPath/shared/confirm-dialog-box-modal/confirm-dialog-box-modal.component';
import { MODAL_OPTIONS } from 'appRootPath/shared/global-meta-data';
import { RejectModalComponent } from 'appRootPath/shared/reject-modal/reject-modal.component';
import { StateMachineActionsService } from './state-machine-actions.service';

@Injectable({
  providedIn: 'root'
})
export class RejectDocumentsService {

  constructor(
    private modalService: NgbModal,
    private stateMachineService: StateMachineActionsService
  ) { }

  /**
   * Handles displaying and closing of rejection modal.
   * @param issueId - ID of the Issue.
   * @param role - User Role type.
   * @param successMessage - Success message for the toaster service.
   * @param callbackFn - Call back function to execute post the move state.
   */
  showRejectionModal(issueId: string, role: RoleTypes, successMessage: string, callbackFn: Function) {
    if (role === 'issuer') {
      const confirmModalRef = this.showConfirmationPopup();
      confirmModalRef.result.then(result => {
        if (result && result === 'confirm') {
          this.showRejectionPopup(issueId, role, successMessage, callbackFn);
        }
      });
    } else {
      this.showRejectionPopup(issueId, role, successMessage, callbackFn);
    }
  }

  /**
   * Handles displaying and closing of rejection modal.
   * @param issueId - ID of the Issue.
   * @param role - User Role type.
   * @param successMessage - Success message for the toaster service.
   * @param callbackFn - Call back function to execute post the move state.
   */
  showRejectionPopup(issueId: string, role: RoleTypes, successMessage: string, callbackFn: Function) {
    const modalRef = this.modalService.open(RejectModalComponent, MODAL_OPTIONS);
    modalRef.componentInstance.userRole = role;

    // Below logic handles watches on modal close
    modalRef.result.then(result => {
      if (result && result.comments) {
        this.handleRejectModalClose(issueId, result, successMessage, callbackFn);
      }
    });
  }

  /**
   * Confirm if the user really wants to reject the documents.
   */
  showConfirmationPopup(): NgbModalRef {
    const confirmModalRef = this.modalService.open(ConfirmDialogBoxModalComponent, MODAL_OPTIONS);

    confirmModalRef.componentInstance.modalData = {
      questionToAsk: `This will regenerate all the existing documents and the documents will have to be signed again.`,
      promptText: `Are you sure you want to Proceed ?`,
      buttons: [
        {
          role: 'cancel',
          name: 'Cancel'
        },
        {
          role: 'confirm',
          name: 'Proceed'
        }
      ]
    };

    return confirmModalRef;
  }


  /**
   * Handles calling of moveStateMachine based on the given reject modal close data.
   * @param issueId - ID of the Issue.
   * @param rejectionData - Data returned on close of Rejection Modal.
   * @param successMessage - Success message for the toaster service.
   * @param callbackFn - Call back function to execute post the move state.
  */
  handleRejectModalClose(issueId: string, rejectionData: any, successMessage: string, callbackFn: Function) {
    const rejectedDocuments = rejectionData.rejected_documents
      .filter(rejectedDoc => Boolean(rejectedDoc.isRejected))
      .map(doc => doc.id);

    const moveStateData = {
      action: 'reject',
      comment: rejectionData.comments,
      rejected_documents: rejectedDocuments
    };

    this.stateMachineService.moveStateMachine(issueId, moveStateData, successMessage, callbackFn);
  }
}
